import { useTranslation } from 'react-i18next';
import { isObjectMergeFields } from '__components/WYSIWYG/utils/merge-fields';
import {
  AIToolbarSection,
  ClearFormattingToolbarSection,
  FontFamilyToolbarSection,
  FontSizeToolbarSection,
  InsertMergeFieldToolbarSection,
  InsertMultiSourceMergeFieldToolbarSection,
  HistoryToolbarSection,
  LinkToolbarSection,
  ListStyleToolbarSection,
  TextAlignToolbarSection,
  TextColorToolbarSection,
  TextStyleToolbarSection,
  HorizontalRuleToolbarSection,
  LineHeightToolbarSection,
} from '../sections/wysiwyg';

import {
  FloatingToolbarWrapper,
  ToolbarWrapper,
  ToolbarSwitch,
} from '../styles';

export const BasicToolBar = ({
  enableAI,
  enableTextStyles,
  enableHorizontalRule,
  enableColor,
  enableHighlight,
  enableHistoryControls,
  enableFontSize,
  defaultFontSize,
  enableFontFamily,
  fonts,
  defaultFontFamily,
  enableLineHeight,
  enableLists,
  enableTextAlign,
  enableLinks,
  textLinkOptions,
  scriptExecutionOptions,
  enableClearFormatting,
  enableMergeFields,
  mergeFields,
  appendMergeFieldExtraSpace,
  onFontFamilyChange,
  onToggleEncoding,
  encodeMergeFieldsInBody,
  enableAllObjectMergeFields,
  wideMergePicker,
}) => {
  const { t } = useTranslation();
  return (
    <ToolbarWrapper isToggleEncoding={Boolean(onToggleEncoding)}>
      {enableTextStyles && (
        <TextStyleToolbarSection
          enableColor={enableColor}
          enableHighlight={enableHighlight}
        />
      )}
      {enableFontSize && (
        <FontSizeToolbarSection defaultFontSize={defaultFontSize} />
      )}
      {enableFontFamily && (
        <FontFamilyToolbarSection
          enableLineHeight={enableLineHeight}
          fonts={fonts}
          defaultFontFamily={defaultFontFamily}
          onChange={onFontFamilyChange}
        />
      )}
      {!enableFontFamily && enableLineHeight && <LineHeightToolbarSection />}
      {enableLists && <ListStyleToolbarSection />}
      {(enableColor || enableHighlight) && (
        <TextColorToolbarSection
          enableColor={enableColor}
          enableHighlight={enableHighlight}
        />
      )}
      {enableTextAlign ? <TextAlignToolbarSection /> : null}
      {enableHorizontalRule ? <HorizontalRuleToolbarSection /> : null}

      {enableLinks && (
        <LinkToolbarSection
          linkOptions={textLinkOptions}
          scriptExecutionOptions={scriptExecutionOptions}
        />
      )}
      {enableClearFormatting && <ClearFormattingToolbarSection />}
      {enableMergeFields ? (
        isObjectMergeFields(mergeFields?.[0]) || enableAllObjectMergeFields ? (
          <InsertMultiSourceMergeFieldToolbarSection
            enableAllObjectMergeFields={enableAllObjectMergeFields}
            appendMergeFieldExtraSpace={appendMergeFieldExtraSpace}
            mergeFields={mergeFields}
            wideMergePicker={wideMergePicker}
          />
        ) : (
          <InsertMergeFieldToolbarSection
            appendMergeFieldExtraSpace={appendMergeFieldExtraSpace}
            mergeFields={mergeFields}
            wideMergePicker={wideMergePicker}
          />
        )
      ) : null}
      {enableAI && <AIToolbarSection />}
      {enableHistoryControls && <HistoryToolbarSection />}
      {onToggleEncoding ? (
        <div>
          <ToolbarSwitch
            className="toggle-encoding"
            textPlacement="left"
            label={t('Encode Merge Fields')}
            checked={encodeMergeFieldsInBody}
            onChange={onToggleEncoding}
            tooltip={t(
              'Escapes merge fields for use in JSON payloads so that special characters won’t result in invalid JSON.'
            )}
          />
        </div>
      ) : null}
    </ToolbarWrapper>
  );
};

export const FloatingToolbar = ({
  target,
  asOverlay,
  zIndex,
  maxToolbarRows,
  toolbarActive,
  enableColor,
  enableHighlight,
  enableHistoryControls,
  enableFontSize,
  defaultFontSize,
  enableFontFamily,
  fonts,
  defaultFontFamily,
  enableAI,
  enableLineHeight,
  enableLinks,
  textLinkOptions,
  scriptExecutionOptions,
  enableClearFormatting,
  enableMergeFields,
  mergeFields,
  onFontFamilyChange,
  allowRelativeLinks,
  enableAllObjectMergeFields,
  wideMergePicker,
}) => {
  return (
    <FloatingToolbarWrapper
      target={target}
      asOverlay={asOverlay}
      zIndex={zIndex}
      maxRows={maxToolbarRows}
      onPointerDown={() => (toolbarActive.current = true)}
      onPointerOver={() => (toolbarActive.current = true)}
      onPointerLeave={() => (toolbarActive.current = false)}
    >
      <TextStyleToolbarSection
        enableColor={enableColor}
        enableHighlight={enableHighlight}
      />
      {enableFontSize && (
        <FontSizeToolbarSection defaultFontSize={defaultFontSize} />
      )}
      {enableFontFamily && (
        <FontFamilyToolbarSection
          enableLineHeight={enableLineHeight}
          fonts={fonts}
          defaultFontFamily={defaultFontFamily}
          onChange={onFontFamilyChange}
        />
      )}
      <ListStyleToolbarSection />
      {(enableColor || enableHighlight) && (
        <TextColorToolbarSection
          enableColor={enableColor}
          enableHighlight={enableHighlight}
        />
      )}
      <TextAlignToolbarSection />
      {enableLinks && (
        <LinkToolbarSection
          linkOptions={textLinkOptions}
          scriptExecutionOptions={scriptExecutionOptions}
          allowRelativeLinks={allowRelativeLinks}
        />
      )}
      {enableClearFormatting && <ClearFormattingToolbarSection />}
      {enableMergeFields ? (
        isObjectMergeFields(mergeFields?.[0]) || enableAllObjectMergeFields ? (
          <InsertMultiSourceMergeFieldToolbarSection
            enableAllObjectMergeFields={enableAllObjectMergeFields}
            mergeFields={mergeFields}
            wideMergePicker={wideMergePicker}
          />
        ) : (
          <InsertMergeFieldToolbarSection
            mergeFields={mergeFields}
            wideMergePicker={wideMergePicker}
          />
        )
      ) : null}
      {enableAI && <AIToolbarSection />}
      {enableHistoryControls && <HistoryToolbarSection />}
    </FloatingToolbarWrapper>
  );
};
